/**
 * Phase 50, BulkImportShellIndicator
 *
 * Tiny pill that renders in the app shell when the bulk-import queue has
 * activity. Lets the user navigate away from /import without losing sight
 * of how many statements are still queuing. Clicking jumps back to /import.
 *
 * Renders nothing when the queue is empty + no in-flight batch.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Files } from 'lucide-react';
import { useBulkImport } from '@/context/BulkImportContext';

export default function BulkImportShellIndicator() {
  const { queue, running, doneSummary } = useBulkImport();

  // Active = a batch is running, OR there are unsubmitted items in queue, OR
  // we just finished and the result summary hasn't been cleared yet.
  const pending = queue.filter((it) => it.status !== 'submitted' && it.status !== 'done' && it.status !== 'failed').length;
  const submitted = queue.filter((it) => it.status === 'submitted' || it.status === 'done').length;
  const total = queue.length;

  if (!running && total === 0 && !doneSummary) return null;

  // Compact summary string
  let label;
  if (running) {
    label = `Importing ${submitted}/${total}`;
  } else if (doneSummary) {
    label = `Imported ${doneSummary.submitted}${doneSummary.failed?.length ? ` · ${doneSummary.failed.length} failed` : ''}`;
  } else if (pending > 0) {
    label = `${pending} queued`;
  } else {
    return null;
  }

  return (
    <Link
      to="/import"
      data-testid="bulk-import-shell-indicator"
      className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/8 text-foreground hover:bg-[hsl(var(--gold))]/14 transition-colors"
      title="Open bulk import"
    >
      {running ? (
        <Loader2 className="h-3 w-3 animate-spin text-[hsl(var(--gold))]" />
      ) : (
        <Files className="h-3 w-3 text-[hsl(var(--gold))]" />
      )}
      <span>{label}</span>
    </Link>
  );
}
